import React, { useEffect, useRef, useState } from 'react';
import './WelcomeExperience.css';

// Approved single source of truth brand asset from master system
import logoStackedUrl from '../../assets/logo/spin-tenet-stacked.svg';
import type {
  WorkerRequest,
  InitWorldPayload,
  ResizeWorldPayload
} from '../../runtime/types';

export interface WelcomeExperienceProps {
  /**
   * Future extensibility hook: Invoked when the user engages the ENTER threshold.
   * Locked to a strict no-op in this phase.
   */
  onEnter?: () => void;
  onEngage?: () => void;
}

/**
 * SPIN TENET — Welcome Experience
 * SIMULATION HEAT-MAP ENTER CONTROL
 *
 * Visual hierarchy:
 *   SPIN TENET identity (mark, wordmark, tagline)
 *           ↓
 *   ENTER (miniature computational heat-map sample window)
 *           ↓
 *   A concept by Dr. Roshan Karan Srivastav (cinematic credit)
 *
 * Sequence:
 *   Phase 1 (0.0s - 2.4s): Procedural world continues normally; zero button presence.
 *   Phase 2 (2.4s): ENTER heat-map field emerges with restrained fade/opacity.
 *   Phase 3 (3.6s): Attribution emerges quietly underneath.
 *
 * Scientific status: EXPERIENTIAL_VISUALIZATION (NOT_A_SCIENTIFIC_RESULT)
 */
export const WelcomeExperience: React.FC<WelcomeExperienceProps> = ({ onEnter, onEngage }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const workerRef = useRef<Worker | null>(null);

  // Phased entrance state
  const [enterVisible, setEnterVisible] = useState(false);
  const [attributionVisible, setAttributionVisible] = useState(false);

  useEffect(() => {
    // Phase 2: ENTER emerges ~2.4s after identity settles
    const enterTimer = window.setTimeout(() => {
      setEnterVisible(true);
    }, 2400);

    // Phase 3: Attribution emerges ~1.2s after ENTER
    const attributionTimer = window.setTimeout(() => {
      setAttributionVisible(true);
    }, 3600);

    return () => {
      window.clearTimeout(enterTimer);
      window.clearTimeout(attributionTimer);
    };
  }, []);

  const handleEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // Phase 4 Enter Threshold: Strictly a no-op action.
    // Architectural hook cleanly preserved for future entrance extension.
    if (typeof onEnter === 'function') {
      onEnter();
    } else if (typeof onEngage === 'function') {
      onEngage();
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create a fresh canvas for each mount lifecycle to prevent
    // 'Cannot transfer control from a canvas for more than one time' under React StrictMode / HMR
    const canvas = document.createElement('canvas');
    canvas.id = 'welcome-world-canvas';
    canvas.className = 'welcome-world-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    container.appendChild(canvas);

    if (typeof canvas.transferControlToOffscreen !== 'function') {
      console.warn('[WelcomeExperience] OffscreenCanvas not supported in this environment.');
      return () => {
        if (container.contains(canvas)) {
          container.removeChild(canvas);
        }
      };
    }

    let offscreen: OffscreenCanvas;
    try {
      offscreen = canvas.transferControlToOffscreen();
    } catch (err) {
      console.warn('[WelcomeExperience] transferControlToOffscreen failed:', err);
      return () => {
        if (container.contains(canvas)) {
          container.removeChild(canvas);
        }
      };
    }

    const worker = new Worker(
      new URL('../../workers/visualization.worker.ts', import.meta.url),
      { type: 'module' }
    );
    workerRef.current = worker;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const initMsg: WorkerRequest<InitWorldPayload> = {
      id: 'init-world',
      type: 'INIT_WORLD',
      payload: {
        canvas: offscreen,
        width,
        height,
        dpr
      }
    };
    worker.postMessage(initMsg, [offscreen]);

    const handleResize = () => {
      if (!workerRef.current) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const rDpr = Math.min(window.devicePixelRatio || 1, 2);

      const resizeMsg: WorkerRequest<ResizeWorldPayload> = {
        id: 'resize-world',
        type: 'RESIZE_WORLD',
        payload: {
          width: w,
          height: h,
          dpr: rDpr
        }
      };
      workerRef.current.postMessage(resizeMsg);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);

      if (workerRef.current) {
        workerRef.current.postMessage({ id: 'stop-world', type: 'STOP_WORLD', payload: null });
        workerRef.current.terminate();
        workerRef.current = null;
      }
      if (container.contains(canvas)) {
        container.removeChild(canvas);
      }
    };
  }, []);

  return (
    <main
      id="welcome-viewport"
      className="welcome-viewport"
      role="main"
      aria-label="SPIN TENET — Welcome"
    >
      {/* Container dynamically hosting the OffscreenCanvas for the procedural computational world */}
      <div
        ref={containerRef}
        className="welcome-canvas-container"
        aria-hidden="true"
      />

      {/* Subtle Static Mineral/Paper Grain Texture Overlay (ultra-restrained, 3.2% opacity) */}
      <svg
        className="welcome-paper-grain"
        aria-hidden="true"
      >
        <filter id="spin-tenet-paper-grain">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.85"
            numOctaves="3"
            stitchTiles="stitch"
          />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#spin-tenet-paper-grain)" />
      </svg>

      {/* Visually hidden semantic heading for accessibility */}
      <h1 className="sr-only">SPIN TENET — A computational doctrine for physical flow.</h1>

      {/* Center Stage: Single Approved SPIN TENET Identity Presentation & Engage Threshold */}
      <section className="welcome-stage" aria-label="SPIN TENET Brand Identity">
        <div className="welcome-identity-lockup">
          <img
            src={logoStackedUrl}
            alt="SPIN TENET — A computational doctrine for physical flow."
            className="welcome-logo"
            draggable={false}
          />

          {/* Enter Threshold: Simulation Heat-Map Doorway & Author Attribution */}
          <div
            className="welcome-threshold"
            aria-live="polite"
          >
            <div className={`welcome-enter-wrapper ${enterVisible ? 'is-visible' : ''}`}>
              <button
                type="button"
                id="spin-tenet-enter-btn"
                className="welcome-enter-btn"
                onClick={handleEnter}
                tabIndex={enterVisible ? 0 : -1}
                aria-label="ENTER"
              >
                {/* Miniature Computational Heat-Map Field */}
                <div className="heatmap-field-canvas" aria-hidden="true">
                  <div className="heatmap-base" />
                  <div className="heatmap-layer-cool" />
                  <div className="heatmap-layer-trans" />
                  <div className="heatmap-layer-shear" />
                  <div className="heatmap-layer-warm" />
                  <div className="heatmap-layer-core" />
                  <div className="heatmap-diffuser" />
                  <div className="heatmap-grid-mesh" />
                </div>

                <span className="enter-label">ENTER</span>
              </button>
            </div>

            <div className={`welcome-attribution-wrapper ${attributionVisible ? 'is-visible' : ''}`}>
              <p className="welcome-attribution">
                A concept by Dr. Roshan Karan Srivastav
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default WelcomeExperience;
